import blogIndex from '../generated/blogIndex';

export interface BlogConversionMeta {
  relatedFirms?: string[];
  primaryPath?: string;
  primaryLabel?: string;
  secondaryPath?: string;
  secondaryLabel?: string;
  eyebrow?: string;
  title?: string;
  description?: string;
}

export interface BlogPostMeta {
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  category: string;
  categorySlug?: string;
  tags?: string[];
  conversion?: BlogConversionMeta;
}

interface BlogPostModule {
  default: unknown;
  meta?: BlogPostMeta;
}

type BlogIndexEntry = BlogPostMeta & {
  sourceFile: string;
};

const blogModules = import.meta.glob<BlogPostModule>('../blog/posts/*.mdx');
const indexedPosts = blogIndex as unknown as BlogIndexEntry[];
const BLOG_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const PROPVIPER_TIMEZONE_OFFSET = '+08:00';

/**
 * Blog dates are editorial publish dates in Malaysia time (UTC+08:00).
 * A post dated 2026-08-10 becomes public at 00:00:00 on 10 Aug in Malaysia.
 */
export function getBlogPublishTimestamp(date: string): number | null {
  const normalizedDate = date.trim();

  if (!BLOG_DATE_PATTERN.test(normalizedDate)) {
    return null;
  }

  const timestamp = Date.parse(`${normalizedDate}T00:00:00${PROPVIPER_TIMEZONE_OFFSET}`);
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function isBlogPostPublished(date: string, now = Date.now()): boolean {
  const publishTimestamp = getBlogPublishTimestamp(date);
  return publishTimestamp !== null && publishTimestamp <= now;
}

const isPublishedEntry = (post: BlogIndexEntry) => isBlogPostPublished(post.date);

const toBlogPostMeta = (post: BlogIndexEntry): BlogPostMeta => ({
  title: post.title,
  slug: post.slug,
  excerpt: post.excerpt,
  author: post.author,
  date: post.date,
  readTime: post.readTime,
  image: post.image,
  category: post.category,
  categorySlug: post.categorySlug,
  tags: post.tags,
  conversion: post.conversion,
});

export async function getBlogPostBySlug(slug: string): Promise<{ meta: BlogPostMeta; Content: unknown } | null> {
  const normalizedSlug = slug.trim().toLowerCase();
  const indexedPost = indexedPosts.find((post) => post.slug.trim().toLowerCase() === normalizedSlug);

  if (!indexedPost) {
    console.warn(`[Blog] Post not found for slug: ${slug}`);
    return null;
  }

  if (!isPublishedEntry(indexedPost)) {
    console.warn(`[Blog] Post is not published yet: ${slug}`);
    return null;
  }

  const modulePath = `../blog/posts/${indexedPost.sourceFile}`;
  const resolver = blogModules[modulePath];

  if (!resolver) {
    console.error(`[Blog] Missing MDX module for indexed post: ${modulePath}`);
    return null;
  }

  try {
    const mod = await resolver();
    if (!mod.meta) {
      console.warn(`[Blog] Missing meta in file: ${modulePath}`);
      return null;
    }

    return {
      meta: toBlogPostMeta(indexedPost),
      Content: mod.default,
    };
  } catch (error) {
    console.error(`[Blog] Error loading post at ${modulePath}:`, error);
    return null;
  }
}

export async function getBlogPosts(): Promise<BlogPostMeta[]> {
  return indexedPosts.filter(isPublishedEntry).map(toBlogPostMeta);
}

export async function getBlogPostsByCategory(categorySlug: string): Promise<BlogPostMeta[]> {
  return indexedPosts
    .filter((post) => isPublishedEntry(post) && post.categorySlug === categorySlug)
    .map(toBlogPostMeta);
}

export async function getRelatedPosts(currentSlug: string, category: string): Promise<BlogPostMeta[]> {
  return indexedPosts
    .filter((post) => isPublishedEntry(post) && post.slug !== currentSlug && post.category === category)
    .slice(0, 3)
    .map(toBlogPostMeta);
}
